import styled from 'styled-components';
import { SPACING_SCALE } from '@/constants/styles';
import { getFontStyles } from '@/redux/cmsConfig/utils';
import { HistoryIcon } from '@/components/uielements/historyIcon';

interface ListProps {
  collapse?: boolean;
}
interface ButtonProps {
  invert?: boolean;
}

const LIST_TRANSITION_TIME = '0.35s';

export const StyledCollapsibleContainer = styled.div<ListProps>`
  max-height: 300px;
  transition:
    max-height ${LIST_TRANSITION_TIME} ease-in-out,
    visibility 0s 0s;

  ${({ collapse }) =>
    collapse &&
    `
    max-height: 0px;
    visibility: hidden;
    transition: max-height ${LIST_TRANSITION_TIME} ease-in-out, visibility 0s ${LIST_TRANSITION_TIME};
  `}
`;

export const StyledReorderList = styled.ul`
  display: flex;
  flex-direction: row;
  gap: var(--size-2);
  margin: 0 var(--neg-size-5) 0 var(--neg-size-5);
  padding: 0;
  overflow-x: auto;

  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    display: none;
    height: 0;
    width: 0;
  }
`;

export const StyledReorderHeading = styled.h2(({ theme }) => ({
  margin: 0,
  ...getFontStyles(theme.location_search.tertiary_font, ['font_family', 'font_size']),
  color: `${theme.buttons.primary_button_background_color}!important`,
}));

export const StyledHistoryIconSvg = styled(HistoryIcon)`
  color: ${({ theme }) => theme.buttons.primary_button_background_color};
  margin-right: 5px;
`;

export const StyledCollapseButton = styled.button<ButtonProps>`
  margin-left: 8px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: var(--size-1);
  position: relative;

  &::after {
    content: '';
    display: block;
    position: relative;
    height: ${SPACING_SCALE.XS}px;
    border-bottom: 1.5px solid ${(props) => props.theme.global.primary_active_color};
    border-right: 1.5px solid ${(props) => props.theme.global.primary_active_color};
    transform: rotate(225deg) translateX(-25%) translateY(-25%);
    transition: transform ${LIST_TRANSITION_TIME} ease-in-out;
    width: ${SPACING_SCALE.XS}px;

    ${({ invert }) =>
      invert &&
      `
      transform: rotate(45deg);
    `}
  }
`;

export const ReorderListPadding = styled.li`
  flex: 0 0 var(--size-3);
  height: var(--size-5);
  list-style-type: none;
  width: var(--size-3);
`;

export const StyledReorderControls = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--size-1);
  margin: 0 0 var(--size-2);
`;

/**
 * Prev/next paging control for the recent-orders row. Mirrors the repo's
 * existing slider button pattern (see the Popular Items `SliderNav`): the same
 * caret icon reused for both directions, rotated 180deg for "back", with the
 * four visual states from the Figma spec — default, hover, focus, disabled.
 */
export const StyledSliderNav = styled.button<{ $back?: boolean }>`
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--size-1);
  border-radius: 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.buttons.primary_button_background_color};
  transition:
    background-color 0.2s ease,
    opacity 0.2s ease;

  svg {
    display: block;
    ${({ $back }) => $back && 'transform: rotate(180deg);'}
  }

  &:hover:not([aria-disabled='true']) {
    background-color: rgba(0, 0, 0, 0.08);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.global.primary_active_color};
    outline-offset: 2px;
  }

  &[aria-disabled='true'] {
    cursor: default;
    opacity: 0.2;
    background-color: transparent;
  }
`;
